import {Calendar, DayOfWeekType, IanaTimeZoneIdType} from "@/store/modules/hcs/calendar/model";
import {DateTime, IANAZone} from "luxon";
import {CALENDAR_DAYS_OF_WEEK, CALENDAR_DAYS_OF_WEEK_ORDINAL, IANA_TZ_IDS} from "@/constants";
import deepEquals from "fast-deep-equal";
import {cloneDeep} from "lodash";
import ErrorMessage from "@/components/molecules/ErrorMessage";
import {
  nthOfWday,
  ordinalToWday,
  sortWday,
  WdayRanges
} from "@/components/pages/implementation/TerminalImplementationList/CalendarUtil";

/** intervalTypeDetail の型 */
type IntervalTypeDetailType = Record<string, {
  text: string;
  overrides: Partial<Calendar>;
}>;

/** endType の型 */
type EndTypeType = 'none' | 'endDate' | 'numberOfOccurrences';

/** CalendarView#source の型 */
interface CalendarViewSource extends Calendar {
  endType?: EndTypeType;
}

/** 平日の曜日 */
const WEEKDAY_DAYS_OF_WEEK = sortWday(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);

/** 全ての曜日 */
const ALL_DAYS_OF_WEEK = sortWday(Object.keys(CALENDAR_DAYS_OF_WEEK) as DayOfWeekType[]);

/**
 * カレンダの扱いを集約したクラス
 */
export default class CalendarView {
  /** システムタイムゾーン */
  static SYSTEM_TIMEZONE = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone as IanaTimeZoneIdType;

  /** カレンダー */
  private src: CalendarViewSource;

  /**
   * コンストラクタ
   *
   * @param source 元となるオブジェクト
   */
  constructor(source: CalendarViewSource) {
    this.src = cloneDeep(source);
  }

  /** ID */
  get id() {
    return this.src.id;
  }

  /** サイトID */
  get terminalId() {
    return this.src.terminalId;
  }

  /** 名称 */
  get name() {
    return this.src.name;
  }
  set name(value) {
    this.src.name = value;
  }

  /** 説明 */
  get description() {
    return this.src.description;
  }
  set description(value) {
    this.src.description = value;
  }

  /** 繰り返すかどうか */
  get isRepeatable(): boolean {
    return this.src.intervalType !== null;
  }
  set isRepeatable(value: boolean) {
    this.src.intervalType = value ? 'minute' : null;
    this.revertToDefault();
  }

  /** 基準日時 (タイムゾーン付日時) */
  get startDateWithTZ() {
    return DateTime.fromISO(this.src.startDate, {zone: this.src.recurrenceTimeZone});
  }

  /** 基準日時 (ローカル日時) */
  get startDateWithoutTZ() {
    return this.startDateWithTZ.setZone('local', {keepLocalTime: true});
  }

  /** 基準日時の曜日 */
  get startDateDayOfWeek() {
    return ordinalToWday(this.startDateWithTZ.weekday);
  }

  /** 基準日時 (ローカル日時) */
  get startDate(): Date {
    return this.startDateWithoutTZ.toJSDate();
  }
  set startDate(jsDate: Date) {
    this.src.startDate = DateTime.fromJSDate(jsDate, {zone: this.src.recurrenceTimeZone}).toISO({includeOffset: false}) ?? '';

    if(this.intervalTypeDetail === null || !this.intervalTypeDetails[this.intervalTypeDetail]) {
      const details = Object.keys(this.intervalTypeDetails);
      if(details.length) {
        this.intervalTypeDetail = details[0];
      }
    }

    this.revertToDefault();
  }

  /**
   * 曜日や週を間隔タイプのデフォルトに設定します
   * @private
   */
  private revertToDefault() {
    switch (this.intervalType) {
      case "minute":
      case "hour":
        this.src.daysOfWeek = ALL_DAYS_OF_WEEK.slice();
        this.src.weeksOfMonth = null;
        break;
      case "week":
        this.src.daysOfWeek = [ordinalToWday(this.startDateWithTZ.weekday)];
        this.src.weeksOfMonth = null;
        break;
      default:
        this.src.daysOfWeek = null;
        this.src.weeksOfMonth = null;
    }
  }

  /** タイムゾーン */
  get recurrenceTimeZone() {
    return this.src.recurrenceTimeZone;
  }
  set recurrenceTimeZone(value) {
    this.src.recurrenceTimeZone = value;
  }

  /** 曜日 */
  get daysOfWeek() {
    return this.src.daysOfWeek?.slice() ?? null;
  }
  set daysOfWeek(values) {
    if(values != null) {
      values = values.slice();
      // 基準日の曜日は外せない
      const weekday = ordinalToWday(this.startDateWithTZ.weekday);
      if(!values.includes(weekday)) {
        values.push(weekday);
      }
      // 順番をソート
      values = sortWday(values);
    }
    this.src.daysOfWeek = values;
  }

  /** 何週目か */
  get weeksOfMonth() {
    return this.src.weeksOfMonth?.slice() ?? null;
  }

  /** 間隔 */
  get interval() {
    return this.src.interval;
  }
  set interval(value) {
    this.src.interval = value;
  }

  /** 間隔タイプ */
  get intervalType() {
    return this.src.intervalType;
  }
  set intervalType(value) {
    this.src.intervalType = value;

    this.revertToDefault();
  }

  /** すべての間隔の詳細設定 */
  get intervalTypeDetails(): IntervalTypeDetailType {
    const startDate = this.startDateWithTZ;
    if(this.src.intervalType === 'month') {
      const startDateNthOfWday = nthOfWday(startDate);
      const startDateDayOfWeek = ordinalToWday(startDate.weekday);
      const result: IntervalTypeDetailType = {
        byDate: {
          text: startDate.toFormat("d '日'"),
          overrides: {
            daysOfWeek: null,
            weeksOfMonth: null,
          }
        },
      };

      if(startDateNthOfWday <= 4) {
        result['byDay'] = {
          text: `第 ${startDateNthOfWday} ${CALENDAR_DAYS_OF_WEEK[startDateDayOfWeek]}曜日`,
          overrides: {
            daysOfWeek: [startDateDayOfWeek],
            weeksOfMonth: [startDateNthOfWday],
          }
        };
      }

      if(startDate.day + 7 >= (startDate?.daysInMonth ?? 0)) {
        result['byLastDay'] = {
          text: `最終 ${CALENDAR_DAYS_OF_WEEK[startDateDayOfWeek]}曜日`,
          overrides: {
            daysOfWeek: [startDateDayOfWeek],
            weeksOfMonth: [5],
          }
        };
      }

      return result;
    }
    return {};
  }

  /** 間隔の詳細設定 */
  get intervalTypeDetail() {
    const cal = this.src;
    if(cal.intervalType === 'month') {
      if(cal.daysOfWeek?.length === 1 && cal.weeksOfMonth?.length === 1) {
        return cal.weeksOfMonth[0] === 5 ? 'byLastDay' : 'byDay';
      } else {
        return 'byDate';
      }
    }
    return null;
  }
  set intervalTypeDetail(v: string | null) {
    if(v === null) {
      return;
    }
    const overrides = this.intervalTypeDetails[v]?.overrides ?? {};
    this.src = {
      ...this.src,
      ...overrides
    }
  }

  /** 終了種別 */
  get endType(): EndTypeType {
    if(this.src.endType) {
      return this.src.endType;
    } else if (this.src.endDate) {
      return 'endDate';
    } else if (this.src.numberOfOccurrences) {
      return 'numberOfOccurrences';
    } else {
      return 'none';
    }
  }
  set endType(value: EndTypeType) {
    this.src.endType = value;
  }

  /** 終了日時 (タイムゾーン付日時) */
  get endDateWithTZ() {
    if(this.src.endDate === null) {
      return null;
    } else {
      return DateTime.fromISO(this.src.endDate, {zone: this.src.recurrenceTimeZone});
    }
  }

  /** 終了日時 (ローカル日時) */
  get endDateWithoutTZ() {
    return this.endDateWithTZ?.setZone('local', {keepLocalTime: true}) ?? null;
  }

  /** 終了日時 (ローカル日時) */
  get endDate() {
    return this.endDateWithoutTZ?.toJSDate() ?? null;
  }
  set endDate(jsDate: Date | null) {
    if(jsDate === null) {
      this.src.endDate = null;
    } else {
      this.src.endDate = DateTime.fromJSDate(jsDate, {zone: this.src.recurrenceTimeZone}).toISO({includeOffset: false});
    }
  }

  /** 繰り返し回数 */
  get numberOfOccurrences() {
    return this.src.numberOfOccurrences;
  }
  set numberOfOccurrences(value) {
    this.src.numberOfOccurrences = value;
  }

  /** サマリ */
  get summary() {
    const src = this.src;
    const startDate = this.startDateWithoutTZ.toFormat('yyyy/LL/dd HH:mm:ss');
    const prefix = (() => {
      if(this.recurrenceTimeZone !== CalendarView.SYSTEM_TIMEZONE) {
        return IANAZone.create(this.recurrenceTimeZone).offsetName(Date.now(), {format: 'long', locale: navigator.language}) + 'で';
      } else {
        return '';
      }
    })();
    const daysOfWeek = this.buildDaysOfWeekSummary();
    const monthPostfix = () => {
      const detail = this.intervalTypeDetail;
      if(detail) {
        return this.intervalTypeDetails[detail]?.text;
      }
      return '';
    };
    const repeatPostfix = (() => {
      switch(this.endType) {
        case "endDate":
          return `に ${this.endDateWithoutTZ?.toFormat('yyyy/LL/dd HH:mm:ss')} まで繰り返し`;
        case "numberOfOccurrences":
          return `に ${this.numberOfOccurrences} 回繰り返し`;
        case "none":
          return `に繰り返し`;
      }
    })();
    switch (src.intervalType) {
      case 'minute':
        return `${prefix} ${startDate} から ${src.interval} 分ごと ${daysOfWeek} ${repeatPostfix}`;
      case 'hour':
        return `${prefix} ${startDate} から ${src.interval} 時間ごと ${daysOfWeek} ${repeatPostfix}`;
      case 'day':
        return `${prefix} ${startDate} から ${src.interval} 日ごと ${repeatPostfix}`;
      case 'week':
        return `${prefix} ${startDate} から ${src.interval} 週間ごと ${daysOfWeek} ${repeatPostfix}`;
      case 'month':
        return `${prefix} ${startDate} から ${src.interval} ヶ月ごと ${monthPostfix()} ${repeatPostfix}`;
      case 'year':
        return `${prefix} ${startDate} から ${src.interval} 年ごと ${repeatPostfix}`;
      default:
        return `${prefix} ${startDate} のみ`;
    }
  }

  /**
   * 曜日のサマリを生成します
   * @private
   */
  private buildDaysOfWeekSummary() {
    const src = this.src;
    const length = src.daysOfWeek?.length ?? 0;
    if(length && length !== 7) {
      const daysOfWeek = src.daysOfWeek as (keyof typeof CALENDAR_DAYS_OF_WEEK)[];
      const daysOfWeekOrdinal = daysOfWeek.map(w => CALENDAR_DAYS_OF_WEEK_ORDINAL[w]);
      if(deepEquals(WEEKDAY_DAYS_OF_WEEK, daysOfWeek)) {
        return '平日';
      }
      return WdayRanges.fromOrdinals(daysOfWeekOrdinal).toString();
    } else {
      return '';
    }
  }

  /** データソース */
  get source() {
    return cloneDeep(this.src);
  }

  /**
   * エラーを検出します
   */
  get errors() {
    const errors: ErrorMessage[] = [];
    const src = this.src;
    if(!src.startDate) {
      errors.push({
        level: 'error',
        message: "基準日時が指定されていません"
      });
    }
    if(!src.recurrenceTimeZone) {
      errors.push({
        level: 'error',
        message: "タイムゾーンが指定されていません"
      });
    } else if(!IANA_TZ_IDS.includes(src.recurrenceTimeZone)) {
      errors.push({
        level: 'error',
        message: `不正なタイムゾーンです: ${src.recurrenceTimeZone}`
      });
    }
    if(src.endDate != null && src.numberOfOccurrences) {
      errors.push({
        level: 'error',
        message: '終了条件に終了日時と繰り返し回数が同時に指定されていますが、この指定方法は対応していません'
      });
    }

    if(this.intervalType !== null) {
      if(src.interval === null) {
        errors.push({
          level: 'error',
          message: '間隔が指定されていません'
        });
      } else if(src.interval <= 0) {
        errors.push({
          level: 'error',
          message: '0 以下の間隔が指定されています'
        });
      }
    }

    const startDateWday = ordinalToWday(this.startDateWithTZ.weekday);
    const startDateNthOfWday = nthOfWday(this.startDateWithTZ);
    const hasDow = (src.daysOfWeek?.length ?? 0) > 0;
    const hasWom = (src.weeksOfMonth?.length ?? 0) > 0;
    switch (this.intervalType) {
      case 'minute':
      case 'hour':
      case 'week':
        if(!hasDow) {
          errors.push({
            level: 'warn',
            message: '曜日が指定されていません。'
          });
        } else if(!src.daysOfWeek?.includes(startDateWday)) {
          errors.push({
            level: 'error',
            message: '曜日指定に「基準日時の曜日」が含まれていません'
          });
        }
        break;
      case 'month':
        if(hasDow && !hasWom) {
          errors.push({
            level: 'warn',
            message: '不正な詳細間隔指定です (週なし)'
          });
        }
        if(!hasDow && hasWom) {
          errors.push({
            level: 'warn',
            message: '不正な詳細間隔指定です (曜日なし)'
          });
        }
        if(hasDow && !src.daysOfWeek?.includes(startDateWday)) {
          errors.push({
            level: 'error',
            message: '曜日指定に「基準日時の曜日」が含まれていません'
          });
        }
        if(hasWom && !src.weeksOfMonth?.includes(startDateNthOfWday)) {
          errors.push({
            level: 'error',
            message: '週の指定に「基準日時の週」が含まれていません'
          });
        }
        break;
    }
    return errors;
  }

  /**
   * 別の CalendarView と比較して同等かどうかを取得します
   *
   * @param other 別の CalendarView
   */
  equals(other: CalendarView) {
    return deepEquals(this.src, other.src);
  }

  /**
   * 永続化用に書き出します
   */
  export() {
    const base: Calendar = {
      id: this.id,
      terminalId: this.terminalId,
      name: this.name,
      description: this.description,
      startDate: this.startDateWithoutTZ.toISO({includeOffset: false}) ?? '',
      recurrenceTimeZone: this.recurrenceTimeZone,
      intervalType: this.intervalType,
      interval: null,
      daysOfWeek: null,
      weeksOfMonth: null,
      endDate: this.endType === 'endDate' ? (this.endDateWithoutTZ?.toISO({includeOffset: false}) ?? null) : null,
      numberOfOccurrences: this.endType === 'numberOfOccurrences' ? this.numberOfOccurrences : null,
    };
    switch (this.intervalType) {
      case 'minute':
      case 'hour':
      case 'week':
        return {
          ...base,
          interval: this.interval,
          daysOfWeek: this.daysOfWeek
        };
      case 'day':
        return {
          ...base,
          interval: this.interval,
        };
      case 'month':
        return {
          ...base,
          interval: this.interval,
          daysOfWeek: this.daysOfWeek,
          weeksOfMonth: this.weeksOfMonth,
        };
      case 'year':
        return {
          ...base,
          interval: this.interval,
        };
      default:
        return base;
    }
  }
}

/**
 * CalendarView を生成します
 *
 * @param calendar Calendar
 */
export function createCalendarView(calendar: Calendar) {
  return new CalendarView(calendar);
}
